import React from 'react';
import styles from './UTMContent.module.css';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';

const URLStructure = () => {
  return (
    <Container style={{ padding: '0px 25px 20px' }}>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', margin: '16px 0', flexWrap: 'wrap' }}>
        <span style={{ marginRight: '8px', fontWeight: 'bold' }}>Form's URL structure:</span>
        <Chip label="Landing Page URL" variant="outlined" style={{ backgroundColor: '#e0f7fa', marginRight: '8px' }} />
        <span>?</span>
        <span>utm_source=</span>
        <Chip label="source" variant="outlined" style={{ backgroundColor: '#ffe0b2', margin: '0 8px' }} />
        <span>&utm_medium=</span>
        <Chip label="medium" variant="outlined" style={{ backgroundColor: '#e1bee7', margin: '0 8px' }} />
        <span>&utm_campaign=</span>
        <Chip label="objective" variant="outlined" style={{ backgroundColor: '#c8e6c9', margin: '0 8px' }} />
        <span>_</span>
        <Chip label="campaign_name" variant="outlined" style={{ backgroundColor: '#c8e6c9', margin: '0 8px' }} />
        <span>&utm_content=</span>
        <Chip label="adset name" variant="outlined" style={{ backgroundColor: '#ffccbc', margin: '0 8px' }} />
        <span>&utm_term=</span>
        <Chip label="ad name" variant="outlined" style={{ backgroundColor: '#bbdefb', margin: '0 8px'  }} />
        <span>_</span>
        <Chip label="date" variant="outlined" style={{ backgroundColor: '#bbdefb', margin: '0 8px'  }} />

      </div>
    </Container>
  );
};

const UTMContent = () => {
  return (
    <section className={styles.container}>
      <header className={styles.header}>
        <h2 className={styles.title}>Google Campaign URL (UTM) Parameters Explained</h2>
        <p className={styles.subtitle}>
          This table explains each Google Analytics UTM campaign parameter, with examples and best practices. Use it to keep naming consistent and make campaign reporting easier to trust in{' '}
          <a href="https://analytics.google.com/analytics/web/" target="_blank" rel="noopener noreferrer" className={styles.link}>
            Google Analytics
          </a>
          .
        </p>

        {/*short, differentiating paragraph (Universal Analytics + governance angle) */}
        <p className={styles.subtitle}>
          This UTM builder is best for teams still using legacy (Universal Analytics) UTMs. The biggest wins come from consistency: one source name per channel, one medium vocabulary, and campaign names that stay readable six months later.
        </p>
      </header>

      <URLStructure />

      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Parameter</th>
            <th className={styles.th}>Required</th>
            <th className={styles.th}>Example</th>
            <th className={styles.th}>Description</th>
          </tr>
        </thead>
        <tbody>

          <tr className={styles.tr}>
            <td className={styles.td}>
              Campaign Source
              <div>
                <Chip label="source" variant="outlined" style={{ backgroundColor: '#ffe0b2' }} />
              </div>
            </td>
            <td className={`${styles.td} ${styles.required}`}>Yes</td>
            <td className={styles.td}>google, facebook, instagram</td>
            <td className={styles.td}>Where your traffic is coming from.</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Campaign Medium
              <div>
                <Chip label="medium" variant="outlined" style={{ backgroundColor: '#e1bee7' }} />
              </div>
            </td>
            <td className={`${styles.td} ${styles.required}`}>Yes</td>
            <td className={styles.td}>cpc, banner, display</td>
            <td className={styles.td}>What type of medium the link is used upon.</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              UTM Objective
              <div>
                <Chip label="objective" variant="outlined" style={{ backgroundColor: '#c8e6c9' }} />
              </div>
            </td>
            <td className={`${styles.td} ${styles.required}`}>Yes</td>
            <td className={styles.td}>awareness (aw), consideration (co), conversion (ko)</td>
            <td className={styles.td}>Objective is prepended to campaign name so you get a consistent UTM campaign structure like objective_campaign.</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Campaign
              <div>
                <Chip label="campaign_name" variant="outlined" style={{ backgroundColor: '#c8e6c9' }} />
              </div>
            </td>
            <td className={`${styles.td} ${styles.required}`}>Yes</td>
            <td className={styles.td}>holiday_sale, summer, new_release</td>
            <td className={styles.td}>The specific promotion or strategy.</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Ad Set Name
              <div>
                <Chip label="adset name" variant="outlined" style={{ backgroundColor: '#ffccbc' }} />
              </div>
            </td>
            <td className={styles.td}>No</td>
            <td className={styles.td}>mens_shoes, genz</td>
            <td className={styles.td}>Known as UTM Content. A sub-group or variant within a campaign.</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Ad Name
              <div>
                <Chip label="ad name" variant="outlined" style={{ backgroundColor: '#bbdefb' }} />
              </div>
            </td>
            <td className={styles.td}>No</td>
            <td className={styles.td}>blue, family_picture</td>
            <td className={styles.td}>Known as UTM Term. Use clear, descriptive terms for the unique name (Note: Ad Name is combined with Date (ie "adname_date") in UTM Term).</td>
          </tr>
          <tr className={styles.tr}>
            <td className={styles.td}>
              Date
              <div>
                <Chip label="date" variant="outlined" style={{ backgroundColor: '#bbdefb' }} />
              </div>
            </td>
            <td className={styles.td}>No</td>
            <td className={styles.td}>01 (January)</td>
            <td className={styles.td}>The release month of the UTM for improved segmentation.</td>
          </tr>
        </tbody>
      </table>
      <p className={styles.subtitle}>
        Need help with UTM campaign-link best practices? Read{' '}
        <a
          href="https://utmsmartmanager.com/how-to-build-store-and-track-utm-campaign-links-like-a-master-digital-marketer/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          How to Build, Store, and Track UTM Campaign Links Like a Master Digital Marketer
        </a>
        .
      </p>
      <p className={styles.caption}>
        About{' '}
        <a
          href="https://utmsmartmanager.com/google-analytics/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          UTM Smart Manager for Google Analytics:
        </a>{' '}
        start with the free Google UTM builder, then move into{' '}
        <a
          href="https://utmsmartmanager.com/campaign-url-studio/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          Campaign URL Studio
        </a>{' '}
        when your team needs shared rules and a personalized Google campaign URL builder. See the{' '}
        <a
          href="https://utmsmartmanager.com/google-analytics/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          Google Analytics solution
        </a>{' '}
        to understand the broader governed workflow. Want campaign data tied to creative performance? Explore{' '}
        <a
          href="https://utmsmartmanager.com/assets-studio/"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.link}
        >
          Assets Studio.
        </a>
      </p>

    </section>
  );
};

export default UTMContent;
